import {Inject, Injectable} from '@angular/core';
import {LIB_AUTH_SERVICE_TOKEN} from '../../components/chatbot/libAuthService';
import {LearnAuthService} from '../../../../../micromate-learn-web/src/app/core/authentication/learn-auth.service';
import {User} from '../../components/chatbot/User';
import {distinctUntilChanged, filter, firstValueFrom, Observable, switchMap} from 'rxjs';
import {BrandingConfig} from './BrandingConfig';
import {OrganizationBrandingResource} from '../rest/organization-branding.resource';
import {shareReplay, tap} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class BrandingService {
    public branding$: Observable<BrandingConfig>;

    constructor(@Inject(LIB_AUTH_SERVICE_TOKEN) private authService: LearnAuthService,
                private organizationBrandingResource: OrganizationBrandingResource) {
        this.branding$ = this.authService.authentication$.pipe(filter((authentication): authentication is User => authentication !== undefined),
            distinctUntilChanged((prev, curr) => prev?.organizationId === curr?.organizationId),
            switchMap((user: User) => organizationBrandingResource.loadLearnerBranding(user.organizationId)),
            shareReplay(1));

        this.branding$.pipe(distinctUntilChanged()).subscribe((branding) => {
            this.applyBrandingColors(branding);
        });
    }

    private applyBrandingColors(branding: BrandingConfig): void {
        const root = document.documentElement;

        Object.entries(branding.colors).forEach(([key, value]) => {
            const cssVarName = `--${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`;
            root.style.setProperty(cssVarName, value);
        });

        root.setAttribute('data-theme', branding.learn.theme);
    }

    public setFavicon(branding: BrandingConfig): void {
        const link = document.querySelector<HTMLLinkElement>('link[rel*=\'icon\']') ?? document.createElement('link');
        link.href = `./assets/branding/${branding.id}/favicon.ico`;
    }

    public async getWidgetBase64(): Promise<string> {
        const branding = await firstValueFrom(this.branding$);
        return branding.learn.logos.widget;
    }

    public setBrandingManually(organizationId?: string): void {
        this.branding$ = this.organizationBrandingResource.loadLearnerProfileBranding(organizationId).pipe(
            distinctUntilChanged(),
            tap(branding => this.applyBrandingColors(branding)),
            shareReplay(1));
    }

    public setTaskbaseBranding(): void {
        this.branding$ = this.organizationBrandingResource.loadTaskbaseBranding().pipe(
            distinctUntilChanged(),
            tap(branding => this.applyBrandingColors(branding)),
            shareReplay(1));
    }

    public setMicromateBranding(): void {
        this.branding$ = this.organizationBrandingResource.loadMicromateBranding().pipe(
            distinctUntilChanged(),
            tap(branding => this.applyBrandingColors(branding)),
            shareReplay(1));
    }
}
